<template>
  <v-dialog v-model="dialog" persistent max-width="600px">
    <v-card>
      <v-card-text>
        <div class="content">
          <div class="settings">
            {{ $t("pallets_maker.pallet_settings") }}
          </div>
          <v-select
            v-model="selectedPackingType"
            :items="packingTypes"
            :label="$t('pallets_maker.packing_type')"
            @input="selectPackingType()"
          ></v-select>
          <v-text-field
            type="number"
            v-model="settings.spacing_sides"
            :label="$t('pallets_maker.sides')"
            :placeholder="placeholder.spacing_sides"
            step="10"
            min="0"
            required
          ></v-text-field>
          <v-text-field
            type="number"
            v-model="settings.spacing_between"
            :label="$t('pallets_maker.between_windows')"
            :placeholder="placeholder.spacing_between"
            step="10"
            min="0"
            required
          ></v-text-field>
          <v-text-field
            type="number"
            v-model="settings.spacing_ends"
            :label="$t('pallets_maker.ends')"
            :placeholder="placeholder.spacing_ends"
            step="10"
            min="0"
            required
          ></v-text-field>
          <v-text-field
            type="number"
            v-model="settings.pallet_height"
            :label="$t('pallets_maker.pallet_height')"
            :placeholder="placeholder.pallet_height"
            step="10"
            min="0"
            required
          ></v-text-field>
          <v-text-field
            type="number"
            v-model="settings.pallet_width"
            :label="$t('pallets_maker.pallet_width')"
            :placeholder="placeholder.pallet_width"
            required
            step="100"
            min="0"
          ></v-text-field>
          <v-text-field
            type="number"
            v-model="settings.max_weight"
            :label="$t('pallets_maker.max_pallet_weight')"
            :placeholder="placeholder.max_weight"
            required
            step="100"
            min="0"
          ></v-text-field>
          <v-text-field
            type="number"
            v-model="settings.max_height"
            :label="$t('pallets_maker.max_pallet_height')"
            :placeholder="placeholder.max_height"
            required
            step="100"
            min="0"
          ></v-text-field>
          <v-switch
            v-if="creating && !temporary"
            v-model="settings.allow_different_orders"
            :label="$t('pallets_maker.allow_different_orders')"
          ></v-switch>
          <v-alert v-if="containsLoaded" type="warning" :value="true">
            {{ $t("pallets_maker.contains_loaded") }}
          </v-alert>
          <v-alert
            v-if="products.length == 0 && creating && !temporary"
            :value="true"
            type="warning"
          >
            {{ $t("pallets_maker.choose_products") }}
          </v-alert>
          <v-alert
            v-if="pallets.length == 0 && !creating"
            :value="true"
            type="warning"
          >
            {{ $t("pallets_maker.choose_pallets") }}
          </v-alert>
          <v-alert v-if="wouldNotFit" :value="true" type="error">
            {{ $t("pallets_maker.would_not_fit") }}
          </v-alert>
          <div class="cancel-button">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn color="red" @click="dialog = false" v-on="on">
                  <v-icon color="white">close</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("pallets_maker.cancel") }}</span>
            </v-tooltip>
          </div>
          <div v-if="creating && !temporary" class="save-pallets-button">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="green"
                  @click="createPallets()"
                  v-on="on"
                  :disabled="createPalletsDisabled"
                >
                  <v-icon color="white">done</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("pallets_maker.confirm") }}</span>
            </v-tooltip>
          </div>
          <div class="save-pallets-button" v-if="temporary">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="green"
                  @click="createTemporaryPallet()"
                  v-on="on"
                  :disabled="!settingsSet"
                >
                  <v-icon color="white">done</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("pallets_maker.create_pallet") }}</span>
            </v-tooltip>
          </div>
          <div v-if="!creating" class="save-pallets-button">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="green"
                  @click="editPallets()"
                  v-on="on"
                  :disabled="!settingsSet || containsLoaded"
                >
                  <v-icon color="white">done</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("pallets_maker.edit_pallets") }}</span>
            </v-tooltip>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import PackingType from "@/models/PackingType.js";
import Product from "@/models/Product.js";
import Pallet from "@/models/Pallet.js";
import Helper from "@/models/Helper.js";
export default {
  props: ["orders", "products", "client"],
  data() {
    return {
      selectedPackingType: null,
      dialog: false,
      packingTypes: this.getPackingTypes(this.$session.get("user")),
      settings: {
        allow_different_orders: true
      },
      defaultSettings: {
        allow_different_orders: true
      },
      placeholder: {},
      creating: true,
      temporary: false,
      user: this.$session.get("user"),
      pallets: [],
      containsLoaded: false,
      loading: false,
      onKeyUp: e => {
        if (e.key === "Escape") {
          this.dialog = false;
        }
        if (e.key === "Enter") {
          this.saveChanges();
        }
      }
    };
  },
  watch: {
    settings: {
      handler() {
        const settings = Object.create(this.settings);
        const packingTypes = this.getPackingTypes(this.user);
        const foundPackingType = packingTypes.find(e => {
          if (e.allow_different_orders != settings.allow_different_orders)
            return false;
          if (e.pallet_height != settings.pallet_height) return false;
          if (e.pallet_weight != settings.pallet_weight) return false;
          if (e.pallet_width != settings.pallet_width) return false;
          if (e.spacing_between != settings.spacing_between) return false;
          if (e.spacing_ends != settings.spacing_ends) return false;
          if (e.spacing_sides != settings.spacing_sides) return false;
          if (e.text != settings.text) return false;
          return true;
        });
        if (foundPackingType) {
          //set name to existing if found
          this.selectedPackingType = foundPackingType.value;
        } else {
          this.selectedPackingType = null; // if not found remove name
        }
      },
      deep: true
    },
    dialog(val) {
      if (val) {
        this.keyUpHandler = this.onKeyUp.bind(this);
        document.addEventListener("keyup", this.keyUpHandler);
      } else {
        this.temporary = false;
        document.removeEventListener("keyup", this.keyUpHandler);
      }
    }
  },
  computed: {
    createPalletsDisabled(){
      if(!this.settingsSet) return true;
      if(this.loading) return true;
      return false;
    },
    settingsSet() {
      if (Object.entries(this.settings) == 0) return false;
      if (!this.creating) return true;
      if (
        this.settings.spacing_sides === null ||
        this.settings.spacing_sides === ""
      )
        return false;
      if (
        this.settings.spacing_between === null ||
        this.settings.spacing_between === ""
      )
        return false;
      if (
        this.settings.spacing_ends === null ||
        this.settings.spacing_ends === ""
      )
        return false;
      if (
        this.settings.pallet_height === null ||
        this.settings.pallet_height === ""
      )
        return false;
      if (
        this.settings.pallet_weight === null ||
        this.settings.pallet_weight === ""
      )
        return false;
      if (
        this.settings.pallet_width === null ||
        this.settings.pallet_width === ""
      )
        return false;
      if (this.settings.max_weight === null || this.settings.max_weight === "")
        return false;
      if (this.settings.max_height === null || this.settings.max_height === "")
        return false;
      if (this.wouldNotFit) return false;
      if (this.creating && !this.temporary) {
        if (this.products.length == 0) return false;
      }
      return true; // if all checks pass enable button
    },
    wouldNotFit() {
      return Product.getHeaviest(this.products) > this.settings.pallet_weight;
    }
  },
  methods: {
    initalize() {
      const order = Object.create(this.orders[0]); // get first order
      if (order.customer != null) {
        if (order.customer.pref_packing_type) {
          this.selectedPackingType = order.customer.pref_packing_type; // selects packing type
          this.selectPackingType(); // sets all properties for selected
          return;
        }
      }
      // if failed to find, get prefered from company
      if (this.user.company.pref_packing_type) {
        this.selectedPackingType = this.user.company.pref_packing_type; // selects packing type
        this.selectPackingType(); //sets all properties for selected packing type
      }
    },
    async createPallets() {
      this.loading = true;
      const products = this.products.slice();
      this.$store.commit('setLoading', true);
      await Pallet.createPallets(products, this.settings);
      this.$store.commit('setLoading', false);
      this.$emit("newPallets");
      this.dialog = false;
    },
    createTemporaryPallet() {
      let tempPallet = {
        id: Math.floor(Math.random() * Math.floor(9999999)),
        spacing_ends: this.settings.spacing_ends,
        spacing_sides: this.settings.spacing_sides,
        spacing_between: this.settings.spacing_between,
        max_weight: this.settings.max_weight,
        max_height: this.settings.max_height,
        length: 1000,
        status: 0,
        width: this.settings.pallet_width,
        pallet_height: this.settings.pallet_height,
        temporary: true,
        owned: true
      };
      const pallet = new Pallet(tempPallet);
      this.$emit("palletCreated", pallet);
      this.dialog = false;
    },
    async editPallets() {
      let breakOut = false;
      this.pallets.forEach(pallet => {
        if (pallet.status == 2) {
          breakOut = true;
        }
      });
      if (breakOut) {
        alert(this.$t("pallets_configurator.editting_not_allowed"));
        return;
      }
      this.$store.commit('setLoading', true);
      await Pallet.recalculatePalletsBySettings(this.pallets, this.settings);
      this.$store.commit('setLoading', false);
      this.pallets = [];
      this.dialog = false;
      this.$emit("palletsUpdated");
      // TODO update truck optimization
    },
    openCreating() {
      this.loading = false;
      this.creating = true;
      this.packingTypes = this.getPackingTypes(this.user);
      this.initalize();
      this.dialog = true;
      this.containsLoaded = false;
    },
    openCreatingTemporary() {
      this.creating = true;
      this.placeholder = {};
      this.packingTypes = this.getPackingTypes(this.user);
      this.temporary = true;
      this.initalize();
      this.dialog = true;
      this.containsLoaded = false;
    },
    openEditting(pallets) {
      this.containsLoaded = false;
      let placeholder = {
        spacing_sides: "",
        spacing_between: "",
        spacing_ends: "",
        pallet_height: "",
        pallet_width: "",
        max_weight: "",
        max_height: ""
      };

      pallets.forEach(pallet => {
        if (pallet.truck_id != null) {
          this.containsLoaded = true;
        }
        placeholder.spacing_sides = Helper.getSettingPlaceholder(
          placeholder.spacing_sides,
          pallet.spacing_sides
        );
        placeholder.spacing_between = Helper.getSettingPlaceholder(
          placeholder.spacing_between,
          pallet.spacing_between
        );
        placeholder.spacing_ends = Helper.getSettingPlaceholder(
          placeholder.spacing_ends,
          pallet.spacing_ends
        );
        placeholder.pallet_height = Helper.getSettingPlaceholder(
          placeholder.pallet_height,
          pallet.pallet_height
        );
        placeholder.pallet_width = Helper.getSettingPlaceholder(
          placeholder.pallet_width,
          pallet.width
        );
        placeholder.max_weight = Helper.getSettingPlaceholder(
          placeholder.max_weight,
          pallet.max_weight
        );
        placeholder.max_height = Helper.getSettingPlaceholder(
          placeholder.max_height,
          pallet.max_height
        );
      });
      this.placeholder = placeholder;
      this.settings = Object.create(this.defaultSettings);
      this.pallets = pallets;
      this.creating = false;
      this.dialog = true;
    },
    selectPackingType() {
      const found = this.packingTypes.find(e => {
        return this.selectedPackingType == e.id;
      });
      if (found) {
        this.settings = found;
      }
    },
    getPackingTypes(user) {
      let packingTypes = [];
      user.company.packing_types.forEach(packingType => {
        packingTypes.push(new PackingType(packingType));
      });
      return packingTypes;
    },
    saveChanges() {
      if (!this.dialog || !this.settingsSet) return;
      if (this.creating && !this.temporary) {
        this.createPallets();
      }
      if (this.temporary) {
        this.createTemporaryPallet();
      }
      if (!this.creating) {
        this.editPallets();
      }
    }
  },
  components: {}
};
</script>

<style scoped>
.content {
  position: relative;
}
.cancel-button {
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>
